import React, { useState, useEffect } from 'react';
import '../housestyle.css';
import './Apply.css';
import * as Icons from '../Icons/Icons';
import PageHero from '../components/PageHero';

const discord_webhooks = {
  helper: 'https://discord.com/api/webhooks/1485290964342935603/RGqJuysIJhjByM1hoArN_XSHcFTM89etDW1JtRhW9rInZF41QFMOmwThgYoPnHIn7nDB',
  developer: 'https://discord.com/api/webhooks/1485290724353114133/SuomQHrvBUZ9mGCHTD7KZ2IaFo3i5iyJdRtHyxhvMP9OxeMSu1ARq_PYxGw5KAtU13MP',
  builder: 'https://discord.com/api/webhooks/1485291117023854673/fhB76q1VL1knwDPYNlwmnohblo7u_VVfIbH4G6Pd1IhIDboLifSs2gDkaaSe1LygXFP9',
};

const video = 'https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_2.mp4'
const cd_applyUser = 24 * 60 * 60 * 1000;
const cooldown_key = 'smc_apply_cooldowns';

function getCooldowns() {
  try { return JSON.parse(localStorage.getItem(cooldown_key) ?? '{}'); }
  catch { return {}; }
}

function setCooldown(roleId) {
  const cd = getCooldowns();
  cd[roleId] = Date.now();
  localStorage.setItem(cooldown_key, JSON.stringify(cd));
}

function getRemainingTime(roleId) {
  const cd = getCooldowns();
  if (!cd[roleId]) return null;
  const remaining = cd_applyUser - (Date.now() - cd[roleId]);
  if (remaining <= 0) return null;
  const h = Math.floor(remaining / 3600000);
  const m = Math.floor((remaining % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const ROLES = [
  { id: 'helper', label: 'Helper', icon: Icons.shield, color: 0xd4920a, desc: 'Support players, moderate chat and handle player reports in-game and in Discord.' },
  { id: 'builder', label: 'Builder', icon: Icons.oakplank, color: 0x52a0e0, desc: 'Design and build POIs, structures, spawn and world features for the server.' },
  { id: 'developer', label: 'Developer', icon: Icons.commandblock, color: 0xa070e0, desc: 'Build custom plugins, configure MythicMobs/ExecutableItems and develop server mechanics.' },
];

const IDENTITY_FIELDS = [
  { key: 'mcName', label: 'Minecraft Username', placeholder: 'For instance S1mpleDImple', required: true },
  { key: 'discordName', label: 'Discord Username', placeholder: 'For instance simpledimple or #1234', required: true },
];

const PLATFORM_OPTIONS = ['Java', 'Bedrock', 'Both'];
const ACCOUNT_OPTIONS = ['Premium', 'Cracked'];

const helper_questions = {
  general: [
    { key: 'timezone', num: 1, label: "What's your timezone, which continent, and what time is it right now?", type: 'input', placeholder: 'For instance GMT+1, Europe, currently 14:30', required: true },
    { key: 'age', num: 2, label: 'How old are you?', type: 'input', placeholder: 'For instance 17', required: true },
    { key: 'activity', num: 3, label: 'How many hours per week can you be active on the server?', type: 'textarea', placeholder: 'Hours per day / week, which days...', required: true, size: 'short' },
    { key: 'playtime', num: 4, label: 'How long have you been playing on SimpleMC?', type: 'input', placeholder: 'For instance 2 weeks, since launch...', required: true },
    { key: 'knowPlayers', num: 5, label: 'Do you know any current players or staff on SimpleMC?', type: 'input', placeholder: 'Names, or "None"', required: true },
  ],
  experience: [
    { key: 'prevStaff', num: 6, label: 'Have you been staff on any other Minecraft servers?', hint: 'If yes — which server, what role, and roughly how long?', type: 'textarea', required: true, size: 'short' },
    { key: 'whyLeft', num: 7, label: 'Did you leave those servers? If so, why?', hint: 'Be honest — resigned, removed, server closed, etc.', type: 'textarea', required: true, size: 'short' },
    { key: 'currentStaff', num: 8, label: 'Are you currently staff on another server right now?', hint: 'If yes, which server and what role', type: 'input', required: true },
    { key: 'strengths', num: 9, label: 'What do you think makes you a good staff member?', hint: 'Be specific — communication, patience, experience, etc.', type: 'textarea', required: true, size: 'short' },
  ],
  situational: [
    { key: 'spammer', num: 10, label: 'A player is spamming chat and ignoring your warnings. What do you do?', type: 'textarea', required: true },
    { key: 'hacker', num: 11, label: "A player reports a hacker but you didn't witness it yourself. How do you handle this?", type: 'textarea', required: true },
    { key: 'staffAbuse', num: 12, label: 'You notice a fellow staff member abusing their permissions. What steps do you take?', type: 'textarea', required: true },
    { key: 'conflict', num: 13, label: 'Two players are arguing aggressively in chat and it is escalating. How do you de-escalate the situation?', type: 'textarea', required: true },
  ],
  final: [
    { key: 'whyUs', num: 14, label: 'Why do you specifically want to be staff on SimpleMC, and not another server?', type: 'textarea', required: true, size: 'tall' },
    { key: 'extra', num: 15, label: 'Anything else you want us to know about you?', type: 'textarea', required: false, placeholder: 'Optional' },
  ],
};

const builder_questions = {
  general: [
    { key: 'timezone', num: 1, label: "What's your timezone, which continent, and what time is it right now?", type: 'input', placeholder: 'For instance GMT+1, Europe, currently 14:30', required: true },
    { key: 'age', num: 2, label: 'How old are you?', type: 'input', placeholder: 'For instance 17', required: true },
    { key: 'activity', num: 3, label: 'How many hours per week can you dedicate to building for the server?', type: 'textarea', placeholder: 'Hours per day / week, which days...', required: true, size: 'short' },
    { key: 'knowPlayers', num: 4, label: 'Do you know any current players or staff on SimpleMC?', type: 'input', placeholder: 'Names, or "None"', required: true },
  ],
  experience: [
    { key: 'buildExp', num: 5, label: 'How long have you been building in Minecraft, and what is your experience level?', hint: 'Beginner, intermediate, expert — be honest', type: 'textarea', required: true },
    { key: 'buildTypes', num: 6, label: 'What styles and types of builds are you most comfortable with?', hint: 'Medieval, modern, organic, terraforming, interiors, large-scale, etc.', type: 'textarea', required: true, size: 'short' },
    { key: 'tools', num: 7, label: 'What tools do you use for building?', hint: 'WorldEdit, FAWE, Litematica, hand-built, etc.', type: 'textarea', required: true, size: 'short' },
    { key: 'portfolio', num: 8, label: 'Do you have a portfolio, screenshots or a showreel of your work?', hint: 'Link an Imgur album, Planet Minecraft, YouTube, Discord screenshots, etc.', type: 'input', required: true },
    { key: 'prevServers', num: 9, label: 'Have you built for any other servers before?', hint: 'Server name, what you built, how long you were there', type: 'textarea', required: true, size: 'short' },
    { key: 'otherServer', num: 10, label: 'Are you currently building for another server right now?', hint: 'If yes, which server', type: 'input', required: true },
  ],
  situational: [
    { key: 'poiIdea', num: 11, label: 'Describe a POI or structure you would build for SimpleMC if given your own choice.', hint: 'Theme, size, gameplay purpose, rough description', type: 'textarea', required: true, size: 'tall' },
    { key: 'feedback', num: 12, label: 'A staff member gives you critical feedback on your build. How do you respond?', type: 'textarea', required: true },
    { key: 'deadline', num: 13, label: 'You are given a deadline for a build but realise you cannot finish in time. What do you do?', type: 'textarea', required: true },
  ],
  final: [
    { key: 'whyUs', num: 14, label: 'Why do you want to build for SimpleMC specifically?', type: 'textarea', required: true, size: 'tall' },
    { key: 'extra', num: 15, label: 'Anything else you want us to know?', type: 'textarea', required: false, placeholder: 'Optional' },
  ],
};

const developer_questions = {
  general: [
    { key: 'timezone', num: 1, label: "What's your timezone, which continent, and what time is it right now?", type: 'input', placeholder: 'For instance GMT+1, Europe, currently 14:30', required: true },
    { key: 'age', num: 2, label: 'How old are you?', type: 'input', placeholder: 'For instance 20', required: true },
    { key: 'activity', num: 3, label: 'How many hours per week can you work on development for the server?', type: 'textarea', placeholder: 'Hours per day / week, which days...', required: true, size: 'short' },
    { key: 'knowPlayers', num: 4, label: 'Do you know any current players or staff on SimpleMC?', type: 'input', placeholder: 'Names, or "None"', required: true },
  ],
  experience: [
    { key: 'javaExp', num: 5, label: 'Do you have experience with Java development?', hint: 'How long, what projects, any published plugins?', type: 'textarea', required: true },
    { key: 'devTypes', num: 6, label: 'What have you developed before?', hint: 'Custom plugins, Skript scripts, configs, datapacks, etc.', type: 'textarea', required: true, size: 'short' },
    { key: 'pluginKnow', num: 7, label: 'How familiar are you with these plugins: ExecutableItems, MythicMobs, CMI, Citizens?', hint: 'Rate each 1-5 and describe what you have done with them', type: 'textarea', required: true },
    { key: 'customMechs', num: 8, label: 'Have you created custom mobs, items or mechanics using MythicMobs or ExecutableItems?', hint: 'If yes, describe what you made and how complex it was', type: 'textarea', required: true },
    { key: 'tools', num: 9, label: 'What development tools do you use regularly?', hint: 'IntelliJ, VSCode, Git, GitHub, server panel, etc.', type: 'textarea', required: true, size: 'short' },
    { key: 'portfolio', num: 10, label: 'Do you have a Portfolio or examples of your work?', hint: 'GitHub link, plugin download, or description of a past project', type: 'input', required: true },
    { key: 'otherServer', num: 11, label: 'Are you currently developing for another server right now?', hint: 'If yes, which server — working for multiple servers is fine if you are transparent', type: 'input', required: true },
  ],
  situational: [
    { key: 'bugFound', num: 12, label: 'You discover a dupe exploit or game-breaking bug on the live server. What do you do? (Tip: We have a development Server)', type: 'textarea', required: true },
    { key: 'codeReview', num: 13, label: 'Another developer pushes code that breaks something in production. How do you handle it?', type: 'textarea', required: true },
    { key: 'playerRequest', num: 14, label: 'A staff member asks you to add a feature. You think it is a bad idea. What do you do?', type: 'textarea', required: true },
  ],
  final: [
    { key: 'whyUs', num: 15, label: 'Why do you want to develop for SimpleMC specifically, and not another server?', type: 'textarea', required: true, size: 'tall' },
    { key: 'extra', num: 16, label: 'Anything else you want us to know?', type: 'textarea', required: false, placeholder: 'Optional' },
  ],
};

const QUESTIONS = { helper: helper_questions, builder: builder_questions, developer: developer_questions };

const SECTION_TITLES = {
  helper: { general: 'General', experience: 'Experience', situational: 'Situational', final: '' },
  builder: { general: 'General', experience: 'Build Experience', situational: 'Situational', final: '' },
  developer: { general: 'General', experience: 'Development Experience', situational: 'Situational', final: '' },
};

const SECTION_ICONS = {
  general: Icons.shield,
  experience: Icons.oakplank,
  situational: Icons.commandblock,
  final: Icons.shield,
};

function buildEmbed(role, identity, answers) {
  const r = ROLES.find(x => x.id === role);
  const qs = Object.values(QUESTIONS[role]).flat();
  const fields = [
    { name: 'Minecraft Username', value: identity.mcName || '-', inline: true },
    { name: 'Discord Username', value: identity.discordName || '-', inline: true },
    { name: 'Platform', value: identity.platform || '-', inline: true },
    { name: 'Account Type', value: identity.accountType || '-', inline: true },
    { name: '\u200B', value: '\u200B', inline: false },
    ...qs
      .filter(q => answers[q.key]?.trim())
      .map(q => ({
        name: `${q.num}. ${q.label}`,
        value: answers[q.key].trim().slice(0, 1024),
        inline: false,
      })),
  ];
  return {
    username: 'SimpleMC Applications',
    embeds: [{
      title: `New ${r.label} Application`,
      color: r.color,
      timestamp: new Date().toISOString(),
      footer: { text: 'SimpleMC Staff Applications · simplemcnetwork.net' },
      fields,
    }],
  };
}

function validate(role, identity, answers) {
  if (!identity.mcName?.trim()) return 'Please enter your Minecraft username.';
  if (!identity.discordName?.trim()) return 'Please enter your Discord username.';
  if (!identity.platform) return 'Please select your platform (Java, Bedrock, or Both).';
  if (!identity.accountType) return 'Please select your account type (Premium or Cracked).';
  const ageVal = parseInt(answers.age, 10);
  if (!isNaN(ageVal) && ageVal < 15) return 'You must be at least 15 years old to apply.';
  const qs = Object.values(QUESTIONS[role]).flat();
  for (const q of qs) {
    if (q.required && !answers[q.key]?.trim()) return `Please answer question ${q.num}.`;
  }
  return null;
}

function StepBar({ step }) {
  return (
    <div className="apply-steps">
      {['Choose Role', 'Application', 'Done'].map((label, i) => {
        const n = i + 1;
        const cls = n < step ? 'done' : n === step ? 'active' : '';
        return (
          <React.Fragment key={label}>
            <div className={`apply-step ${cls}`}>
              <div className="apply-step-num">{n < step ? '✓' : n}</div>
              {label}
            </div>
            {i < 2 && <div className="apply-step-line" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FormField({ q, value, onChange }) {
  return (
    <div className="apply-field">
      <div className="apply-field-label">
        <span className="apply-field-num">{q.num}.</span>
        {q.label}
        {q.required && <span className="apply-req"> </span>}
      </div>
      {q.hint && <div className="apply-hint">{q.hint}</div>}
      {q.type === 'input' ? (
        <input
          className="apply-input"
          placeholder={q.placeholder ?? ''}
          value={value}
          onChange={e => onChange(q.key, e.target.value)}
        />
      ) : (
        <>
          <textarea
            className={`apply-textarea ${q.size ?? ''}`}
            placeholder={q.placeholder ?? ''}
            value={value}
            onChange={e => onChange(q.key, e.target.value)}
          />
          {q.required && (
            <div className={`apply-char ${value.length >= 40 ? 'ok' : ''}`}>
              {value.length} chars {value.length >= 40 ? '✓' : '(min 40)'}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RoleIcon({ src, alt }) {
  return (
    <img
      src={src}
      alt={alt}
      className="apply-role-icon-img"
    />
  );
}

function SectionIcon({ src }) {
  return (
    <img
      src={src}
      alt=""
      className="apply-section-icon-img"
    />
  );
}

function PageHeader() {
  return (
    <PageHero video={video} eyebrow="Staff Team" title="Apply for " em="Staff" subtitle="Fill out the form! Your application will get sent to our Discord staff channel. Low-effort answers will be declined." />
  );
}

export default function Apply() {
  const [step, setStep] = useState(1);
  const [role, setRole] = useState('');
  const [identity, setIdentity] = useState({ mcName: '', discordName: '', platform: '', accountType: '' });
  const [answers, setAnswers] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [cooldowns, setCooldowns] = useState({});

  useEffect(() => { setCooldowns(getCooldowns()); }, []);

  const setAnswer = (key, val) => { setAnswers(a => ({ ...a, [key]: val })); setError(''); };
  const setIdField = (key, val) => { setIdentity(i => ({ ...i, [key]: val })); setError(''); };

  async function submit() {
    const err = validate(role, identity, answers);
    if (err) { setError(err); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setSending(true); setError('');
    try {
      const res = await fetch(discord_webhooks[role], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildEmbed(role, identity, answers)),
      });
      if (res.ok || res.status === 204) {
        setCooldown(role);
        setCooldowns(getCooldowns());
        setStep(3);
        window.scrollTo(0, 0);
      } else {
        setError(`Discord error (${res.status}) — check the webhook URL.`);
      }
    } catch {
      setError('Network error — check the webhook URL and try again.');
    } finally {
      setSending(false);
    }
  }

  function reset() {
    setStep(1); setRole(''); setIdentity({ mcName: '', discordName: '', platform: '', accountType: '' });
    setAnswers({}); setError('');
  }

  const roleObj = ROLES.find(r => r.id === role);
  const questions = role ? QUESTIONS[role] : {};

  if (step === 1) return (
    <>
      <PageHeader />
      <div className="container apply-form-wrap">
        <StepBar step={1} />
        <div className="section-label"><span className="sl-text">Choose your role</span></div>
        <div className="apply-role-grid">
          {ROLES.map(r => {
            const remaining = getRemainingTime(r.id);
            return (
              <div
                key={r.id}
                className={`apply-role-card ${role === r.id ? 'selected' : ''} ${remaining ? 'cooldown' : ''}`}
                onClick={() => !remaining && setRole(r.id)}
              >
                <div className="apply-role-card-bar" />
                <RoleIcon src={r.icon} alt={r.label} />
                <div className="apply-role-name">{r.label}</div>
                <div className="apply-role-desc">{r.desc}</div>
                {remaining && (
                  <span className="apply-cooldown-badge">Try again in {remaining}</span>
                )}
              </div>
            );
          })}
        </div>
        <button
          className={`btn btn-primary ${!role ? 'apply-btn-disabled' : ''}`}
          onClick={() => role && setStep(2)}
        >
          Next Step
        </button>
      </div>
    </>
  );

  if (step === 3) return (
    <>
      <PageHeader />
      <div className="container">
        <div className="apply-success">
          <div className="apply-success-icon">
            <RoleIcon src={roleObj?.icon} alt={roleObj?.label} />
          </div>
          <h2>Application Submitted</h2>
          <p>
            Your <strong className="apply-success-highlight">{roleObj?.label}</strong> application
            has been posted to our staff Discord channel. We will review it and reach out via Discord — keep your DMs open.
          </p>
          <div className="apply-success-summary">
            {[
              ['Role', roleObj?.label],
              ['Minecraft Username', identity.mcName],
              ['Discord Username', identity.discordName],
              ['Timezone', answers.timezone],
            ].map(([l, v], i) => (
              <div key={i} className="apply-success-row">
                <span className="apply-sr-label">{l}</span>
                <span className="apply-sr-val">{v ?? '-'}</span>
              </div>
            ))}
          </div>
          <button className="btn btn-secondary" onClick={reset}>
            Submit Another Application
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <PageHeader />
      <div className="container apply-form-wrap">
        <StepBar step={2} />

        <div className="apply-role-bar">
          <div className="apply-role-bar-left">
            <div className="section-label apply-section-label-inline">
              <span className="sl-text">Your Application</span>
            </div>
            <span className="apply-role-pill">
              <RoleIcon src={roleObj?.icon} alt={roleObj?.label} />
              {roleObj?.label}
            </span>
          </div>
          <button className="btn btn-secondary apply-change-btn" onClick={() => setStep(1)}>
            Go back
          </button>
        </div>

        <div className="apply-warning">
          Application with low-effort answers will not be taken seriously, so please put thought into your responses. We will reach out via Discord, so keep your DMs open!
        </div>

        {error && <div className="apply-error">{error}</div>}

        <div className="apply-identity">
          <div className="apply-identity-title">
            Your Details
            <span className="apply-min-age-badge">Min Age: 15+</span>
          </div>
          {IDENTITY_FIELDS.map(f => (
            <div key={f.key} className="apply-field">
              <div className="apply-field-label">
                {f.label}
                <span className="apply-req"></span>
              </div>
              <input
                className="apply-input"
                placeholder={f.placeholder}
                value={identity[f.key]}
                onChange={e => setIdField(f.key, e.target.value)}
              />
            </div>
          ))}
          <div className="apply-field apply-toggle-field">
            <div className="apply-field-label">
              Platform <span className="apply-req"></span>
            </div>
            <div className="apply-toggle-group">
              {PLATFORM_OPTIONS.map(opt => (
                <button
                  key={opt}
                  type="button"
                  className={`apply-toggle-btn ${identity.platform === opt ? 'active' : ''}`}
                  onClick={() => { setIdField('platform', opt); setError(''); }}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div className="apply-field apply-toggle-field">
            <div className="apply-field-label">
              Account Type <span className="apply-req"></span>
            </div>
            <div className="apply-toggle-group">
              {ACCOUNT_OPTIONS.map(opt => (
                <button
                  key={opt}
                  type="button"
                  className={`apply-toggle-btn ${identity.accountType === opt ? 'active' : ''}`}
                  onClick={() => { setIdField('accountType', opt); setError(''); }}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>

        {Object.entries(questions).map(([sectionKey, qs]) => (
          <div key={sectionKey} className="apply-section">
            {SECTION_TITLES[role][sectionKey] && (
              <div className="apply-section-header">
                <SectionIcon src={SECTION_ICONS[sectionKey]} />
                <span className="apply-section-title">{SECTION_TITLES[role][sectionKey]}</span>
              </div>
            )}
            {qs.map(q => (
              <FormField
                key={q.key}
                q={q}
                value={answers[q.key] ?? ''}
                onChange={setAnswer}
              />
            ))}
          </div>
        ))}

        {error && <div className="apply-error">{error}</div>}

        <div className="apply-submit-row">
          <button
            className={`btn btn-primary apply-submit-btn ${sending ? 'apply-btn-disabled' : ''}`}
            onClick={submit}
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Submit Application'}
          </button>
          <span className="apply-submit-note">After sending open a ticket if you haven't already</span>
        </div>

        <div className="apply-disclaimer">
          By submitting you confirm all information is accurate. Lying about past bans or experience is an automatic disqualification. We will reach out via Discord — make sure your DMs are open or that you have a ticket open.
        </div>
      </div>
    </>
  );
}